/* Header, sidebar, depot scope switcher, theme toggle and the offline banner.

   The navigation mirrors the prototype's grouping exactly — Overview, Register,
   Infrastructure, Documentation, Settings — because that grouping is part of the
   spec, not decoration. */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { AUTH_MODE } from '../auth/msalConfig';
import { DepotScopeContext } from '../hooks/useDepotScope';
import { groupOf } from '../lib/deviceGroups';
import { useDepots } from '../hooks/useDepots';
import { useOnline } from '../hooks/useOnline';
import { useTheme } from '../hooks/useTheme';
import { buildExpiries, dueWithin } from '../lib/expiries';
import { initials } from '../lib/format';
import { isOffboarding, isOnboarding } from '../lib/lifecycle';
import { headcount, visibleLicences } from '../lib/registers';
import type { DepotScope } from '../models';
import { FIXED_MODULES, moduleOf } from '../lib/modules';
import { PRODUCT } from '../lib/product';
import { qk } from '../query/keys';
import { useServices } from '../services/context';
import { AttentionBell } from './AttentionBell';
import { GlobalSearch } from './GlobalSearch';
import { UpdatePrompt } from './UpdatePrompt';
import s from './AppShell.module.scss';
import { HUB_URL, shouldStepBack } from '../lib/hub';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Key into the counts map. */
  count?: string;
  alert?: boolean;
  /** A count that is not one of the fixed ones — used by the custom registers,
      whose names are not known until somebody defines them. */
  badge?: number;
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: 'ti-layout-dashboard' },
      { to: '/expiries', label: 'Expiries', icon: 'ti-calendar-exclamation', count: 'expiries', alert: true },
    ],
  },
  {
    label: 'Register',
    items: [
      { to: '/people', label: 'Accounts', icon: 'ti-users', count: 'people' },
      /* alert, because the badge IS the flag. A new account appears in the
         register on its own; nothing else tells anybody it is waiting. */
      { to: '/onboarding', label: 'Onboarding', icon: 'ti-user-plus', count: 'onboarding', alert: true },
      /* Its own entry, with its own badge. Leavers used to be counted under
         Onboarding, which put a flag on "Onboarding" for people who were leaving. */
      { to: '/offboarding', label: 'Offboarding', icon: 'ti-user-minus', count: 'offboarding', alert: true },
      { to: '/groups', label: 'Groups', icon: 'ti-users-group', count: 'groups' },
      { to: '/devices', label: 'Devices', icon: 'ti-device-laptop', count: 'devices' },
      { to: '/infrastructure', label: 'Infrastructure', icon: 'ti-server-2', count: 'infrastructure' },
      { to: '/printers', label: 'Printers', icon: 'ti-printer', count: 'printers' },
      { to: '/licences', label: 'Licences', icon: 'ti-license', count: 'licences' },
      { to: '/import', label: 'Import', icon: 'ti-upload' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { to: '/locations', label: 'Locations', icon: 'ti-map-pin', count: 'locations' },
      { to: '/network', label: 'Network', icon: 'ti-network', count: 'networks' },
      { to: '/applications', label: 'Applications', icon: 'ti-apps', count: 'applications' },
      { to: '/suppliers', label: 'Suppliers', icon: 'ti-building-store', count: 'suppliers' },
      { to: '/domains', label: 'Domains & SSL', icon: 'ti-world', count: 'domains' },
      { to: '/backup-dr', label: 'Backup & DR', icon: 'ti-shield-bolt', count: 'backups' },
    ],
  },
  {
    label: 'Documentation',
    items: [
      { to: '/knowledge-base', label: 'Knowledge base', icon: 'ti-book-2', count: 'articles' },
      { to: '/procedures', label: 'Procedures', icon: 'ti-checklist', count: 'runs' },
      { to: '/credentials', label: 'Credentials', icon: 'ti-key', count: 'credentials' },
      { to: '/audit', label: 'Audit trail', icon: 'ti-history' },
      { to: '/pack', label: 'Offline pack', icon: 'ti-printer' },
      { to: '/labels', label: 'Asset labels', icon: 'ti-qrcode' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/modules', label: 'Modules', icon: 'ti-puzzle' },
      { to: '/notifications', label: 'Notifications', icon: 'ti-bell' },
      { to: '/record-types', label: 'Record types', icon: 'ti-layout-grid-add', count: 'recordTypes' },
      { to: '/roles', label: 'Roles', icon: 'ti-users-group' },
    ],
  },
];

/** Sidebar counts. One place, so the badges cannot drift from the screens. */
function useNavCounts(): Record<string, number> {
  const svc = useServices();
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const groups = useQuery({ queryKey: qk.groups, queryFn: () => svc.groups.all() });
  const licences = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const backups = useQuery({ queryKey: qk.backups, queryFn: () => svc.backups.jobs() });
  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });
  const runs = useQuery({ queryKey: qk.runs, queryFn: () => svc.procedures.runs() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });
  const recordTypes = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  const customRecords = useQuery({ queryKey: qk.customRecords(), queryFn: () => svc.recordTypes.records() });

  return useMemo(() => {
    const expiries = buildExpiries({
      devices: devices.data,
      people: people.data,
      suppliers: suppliers.data,
      domains: domains.data,
      applications: applications.data,
      credentials: credentials.data,
      articles: articles.data,
      skus: visibleLicences(licences.data ?? []),
      recordTypes: recordTypes.data,
      customRecords: customRecords.data,
    });

    return {
      people: people.data?.length ?? 0,
      /* Derived from the people query rather than a query of its own — the
         queue is a filter over a list the sidebar already holds. */
      onboarding: (people.data ?? []).filter(isOnboarding).length,
      offboarding: (people.data ?? []).filter(isOffboarding).length,
      groups: groups.data?.length ?? 0,
      devices: (devices.data ?? []).filter((d) => groupOf(d.type) === 'endpoint').length,
      printers: (devices.data ?? []).filter((d) => d.type === 'Printer').length,
      infrastructure: (devices.data ?? []).filter((d) => groupOf(d.type) === 'infrastructure').length,
      licences: visibleLicences(licences.data ?? []).length,
      networks: 3,
      locations: 3,
      applications: applications.data?.length ?? 0,
      suppliers: suppliers.data?.length ?? 0,
      domains: domains.data?.length ?? 0,
      backups: backups.data?.length ?? 0,
      articles: articles.data?.length ?? 0,
      runs: runs.data?.length ?? 0,
      credentials: credentials.data?.length ?? 0,
      recordTypes: recordTypes.data?.length ?? 0,
      expiries: dueWithin(expiries, 90).length,
    };
  }, [
    people.data,
    groups.data,
    devices.data,
    licences.data,
    applications.data,
    suppliers.data,
    domains.data,
    backups.data,
    articles.data,
    runs.data,
    credentials.data,
    recordTypes.data,
    customRecords.data,
  ]);
}

function Banner({ tone, icon, children }: { tone: 'warn' | 'info'; icon: string; children: ReactNode }) {
  return (
    <div className={`${s.banner} ${tone === 'warn' ? s.bannerWarn : s.bannerInfo}`} data-print="hide">
      <i className={`ti ${icon}`} aria-hidden />
      <div>{children}</div>
    </div>
  );
}

export function AppShell() {
  const svcForSites = useServices();
  /* The site addresses, which used to be a constant in the source. Read here
     so the tooltip on a depot button is something somebody typed. */
  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svcForSites.locations.all() });

  /* Closed by clicking anywhere else, the same way the search results are. A
     menu that only closes by pressing the thing that opened it is a menu people
     leave open. */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const online = useOnline();
  const counts = useNavCounts();
  const location = useLocation();
  const [scope, setScope] = useState<DepotScope>('all');
  const [sideOpen, setSideOpen] = useState(false);

  const svc = useServices();
  const depots = useDepots();
  const scopeValue = useMemo(() => ({ scope, setScope }), [scope]);

  /* Head-count per depot, for the switcher's small numbers. Leavers are not
     "at" a depot in any sense that matters here. */
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const depotHeadcount = (id: string) =>
    headcount((people.data ?? []).filter((p) => p.loc === id));

  /* Custom registers in the sidebar. §6.2 calls record types "what makes the
     system Simon's rather than the builder's", and a type you have to go into
     Settings to reach is still the builder's. Same query keys the counts use,
     so TanStack serves both from one fetch. */
  const recordTypes = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  /* The module switches, which until now were a screen writing a flag nothing
     read. Turning a section off did nothing at all, while the screen said it
     hid it. */
  const modules = useQuery({ queryKey: qk.modules, queryFn: () => svc.access.modules() });
  const allRecords = useQuery({
    queryKey: qk.customRecords(),
    queryFn: () => svc.recordTypes.records(),
  });

  const nav = useMemo<NavGroup[]>(() => {
    /* Off by explicit flag only.
     *
     * A module missing from the list is shown, not hidden — that is the state
     * on a site that has never been seeded, and defaulting to hidden there
     * would leave somebody with an empty sidebar and no way to find out why. A
     * switch has to be thrown to remove anything. */
    const off = new Set(
      (modules.data ?? []).filter((m) => !m.enabled).map((m) => m.id),
    );

    const shown = (to: string) =>
      FIXED_MODULES.has(moduleOf(to)) || !off.has(moduleOf(to));

    const enabled = (groups: NavGroup[]) =>
      groups
        .map((g) => ({ ...g, items: g.items.filter((i) => shown(i.to)) }))
        // A group whose every section is off should not leave its heading behind.
        .filter((g) => g.items.length > 0);

    const custom = (recordTypes.data ?? [])
      .filter((t) => t.enabled)
      .map((t) => ({
        to: `/records/${t.id}`,
        label: t.title,
        icon: t.icon || 'ti-file-text',
        badge: (allRecords.data ?? []).filter((r) => r.typeId === t.id).length,
      }));

    if (custom.length === 0) return enabled(NAV);

    /* Before Settings — a register somebody defined is content, not
       configuration, and belongs with the other registers. */
    const at = NAV.findIndex((g) => g.label === 'Settings');
    const groups = [...NAV];
    groups.splice(at < 0 ? groups.length : at, 0, { label: 'Your registers', items: custom });
    return enabled(groups);
  }, [recordTypes.data, allRecords.data, modules.data]);

  return (
    <DepotScopeContext.Provider value={scopeValue}>
      <div className={s.shell}>
        <header className={s.header}>
          <button
            type="button"
            className={s.burger}
            onClick={() => setSideOpen((v) => !v)}
            aria-label="Menu"
          >
            <i className="ti ti-menu-2" aria-hidden />
          </button>

          {/* The mark and the app's name, and nothing else.

              It was a 44px logo with a drop shadow, "Hird BluePrint", and a
              strapline underneath. Every other app in the suite shows a flat
              34px mark and the product name on one line — Total Safe's
              stylesheet calls the raised tile "the single most obvious
              difference between two headers side by side", and this was the
              app still doing it.

              PRODUCT.name, not fullName: the company prefix belongs on a
              sign-in screen or an asset label, not on the bar of an app you
              are already inside. The strapline said what the app is to
              somebody who is using it. */}
          <Link to="/" className={s.brand}>
            <img src={PRODUCT.logo} alt="" width={34} height={34} />
            <b>{PRODUCT.name}</b>
          </Link>

          <GlobalSearch />

          <div className={s.depotsw} role="group" aria-label="Depot scope">
            <button
              type="button"
              className={`${s.depotBtn} ${scope === 'all' ? s.depotOn : ''}`}
              onClick={() => setScope('all')}
            >
              <i className="ti ti-building-community" aria-hidden />
              All
            </button>
            {depots.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`${s.depotBtn} ${scope === d.id ? s.depotOn : ''}`}
                onClick={() => setScope(d.id)}
                title={locations.data?.[d.id]?.addr || undefined}
              >
                {d.name}
                <span className={s.dcount}>{depotHeadcount(d.id)}</span>
              </button>
            ))}
          </div>

          {/* The way back, matching SignIn's.

              Blueprint opens as its own site from a Hub tile, so without this
              the only exit is the browser's Back button. A button to look at
              with an anchor underneath: middle-click and ctrl-click still open
              the hub in a new tab, and the href is the route for anybody who
              came here directly rather than through a tile. */}
          <a
            className={s.toHub}
            href={HUB_URL}
            title="Back to the Hird Hub"
            onClick={(e) => {
              if (!shouldStepBack(e)) return;
              e.preventDefault();
              window.history.back();
            }}
          >
            <i className="ti ti-arrow-left" aria-hidden />
            <span>Hub</span>
          </a>

          <AttentionBell />

          {/* Theme sits in the bar, not under the avatar.

              Every other app in the suite puts the same three controls in the
              same order on the right of its header — Hub, theme, you — and
              Blueprint had the toggle hidden one click deep inside the account
              menu. Same order, same shapes as Total Safe's. */}
          <button
            type="button"
            className={s.themeBtn}
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title="Theme"
          >
            <i className={`ti ${theme === 'dark' ? 'ti-sun' : 'ti-moon'}`} aria-hidden />
          </button>

          {/* Everything that is about you rather than about the register.
              The bar used to carry Import, a theme toggle, a two-line name block
              and a sign-out icon in four different button treatments; Import is
              in the nav already, and the other three are things somebody looks
              for under their own avatar. */}
          <div className={s.who} ref={menuRef}>
            <button
              type="button"
              className={s.whoBtn}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={user?.name ?? 'Account'}
            >
              <span className={s.avatar}>{initials(user?.name ?? '?')}</span>
              <i className="ti ti-chevron-down" aria-hidden />
            </button>

            {menuOpen && (
              <div className={s.whoMenu} role="menu">
                <div className={s.whoWho}>
                  <b>{user?.name ?? 'Not signed in'}</b>
                  <span>{user?.role ?? '—'}</span>
                </div>

                {/* Sign out only. Signed-out never reaches here — AuthProvider
                    gates the whole app on an account. */}
                {AUTH_MODE === 'msal' && (
                  <button type="button" className={s.whoItem} onClick={signOut} role="menuitem">
                    <i className="ti ti-logout" aria-hidden />
                    Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        {sideOpen && <div className={s.sideScrim} onClick={() => setSideOpen(false)} />}

        <nav className={`${s.side} ${sideOpen ? s.sideOpen : ''}`} aria-label="Sections">
          {nav.map((group, gi) => (
            <div key={group.label ?? `g${gi}`}>
              {group.label && <div className={s.sideLabel}>{group.label}</div>}
              {group.items.map((item) => {
                const count = item.badge ?? (item.count ? counts[item.count] : undefined);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `${s.nav} ${isActive ? s.navOn : ''}`}
                    onClick={() => setSideOpen(false)}
                  >
                    <i className={`ti ${item.icon}`} aria-hidden />
                    {item.label}
                    {count !== undefined && (
                      <span className={`${s.count} ${item.alert && count > 0 ? s.countAlert : ''}`}>{count}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <UpdatePrompt />
        <main className={s.main} key={location.pathname}>
          <div className={s.mainInner}>
            {!online && (
              <Banner tone="warn" icon="ti-cloud-off">
                You are offline. Showing data from the last sync — edits are disabled until you are back
                on a network.
              </Banner>
            )}
            {AUTH_MODE === 'mock' && (
              <Banner tone="info" icon="ti-flask">
                Running without sign-in. Set <code>VITE_AUTH_MODE=msal</code> once the Entra app
                registration exists — see <code>docs/entra-setup.md</code>.
              </Banner>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </DepotScopeContext.Provider>
  );
}
